import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowUpRight } from "@phosphor-icons/react";
import SectionHeading from "@/components/SectionHeading";
import { STATS, TEAM, CERTIFICATIONS, COMPANY } from "@/data/content";

const VALUES = [
  {
    title: "Security by Design",
    body:
      "Every system we ship is threat-modelled, hardened and pen-tested before it touches production traffic.",
  },
  {
    title: "Intelligence Built In",
    body:
      "We weave AI/ML and analytics into our products and engagements — turning operational data into measurable decisions.",
  },
  {
    title: "Engineering Discipline",
    body:
      "Clean architecture, observability, automated testing and weekly demos. Predictable delivery, every sprint.",
  },
  {
    title: "Long-term Partnership",
    body:
      "We optimise for trust over transactions — many of our clients have been with us for 4+ years and counting.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-5">About Theta Dynamics</div>
            <h1
              data-testid="about-hero-title"
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900"
            >
              Engineering across <span className="text-[#1D4ED8]">Cyber Security, AI/ML, ERP, AR/VR & Surveillance.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
              Theta Dynamics is a deep-tech engineering company. We design, build and operate secure, intelligent and immersive systems for enterprises, governments and institutions — across India and abroad.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-20 md:py-32 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-5">
          <div className="card-flat p-8 md:p-12">
            <div className="overline mb-4">Mission</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
              Engineer secure, intelligent and immersive systems — at scale.
            </h3>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Theta Dynamics' mission is to deliver production-grade solutions across Cyber Security, AI/ML, ERP, AR/VR and Surveillance. We protect, automate and visualise mission-critical operations for clients who cannot afford downtime — combining strong engineering practice with a security-first mindset.
            </p>
          </div>

          <div className="card-flat p-8 md:p-12">
            <div className="overline mb-4">Vision</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
              The default partner for secure & immersive deep-tech.
            </h3>
            <p className="mt-5 text-slate-600 leading-relaxed">
              To be the default deep-tech partner for organisations modernising their security, data and operational stacks — recognised globally for our engineering rigour, our AI/ML and AR/VR craftsmanship, and our uncompromising stance on security.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-200">
          {STATS.map((s) => (
            <div key={s.label} className="px-4 py-8">
              <div className="font-display text-4xl md:text-5xl font-black text-slate-900 tracking-tighter">{s.value}</div>
              <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Core values"
            title="Principles that govern every line of code we ship."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="card-flat p-7 md:p-8"
              >
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1D4ED8] font-bold">
                  0{i + 1}
                </div>
                <h4 className="mt-4 font-display text-lg font-semibold text-slate-900 tracking-tight">
                  {v.title}
                </h4>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed font-light">
                  {v.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-32 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Leadership"
            title="The technologists behind Theta Dynamics."
            description="A founding team that has shipped real systems across cyber security, AI/ML, ERP, AR/VR and surveillance — supported by senior advisors."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TEAM.map((m) => (
              <div
                key={m.name}
                data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="card-flat p-7 md:p-8 flex flex-col"
              >
                <div className="w-full aspect-[4/5] bg-slate-100 border border-slate-200 mb-6 grid place-items-center overflow-hidden">
                  {m.image ? (
                    <img
                      src={m.image}
                      alt={m.name}
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-[#1D4ED8] grid place-items-center font-display font-black text-white text-3xl">
                      {m.avatarLetter}
                    </div>
                  )}
                </div>
                <div className="font-display text-lg font-semibold text-slate-900 tracking-tight leading-snug">
                  {m.name}
                </div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#1D4ED8] font-bold">
                  {m.role}
                </div>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed font-light">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="card-flat overflow-hidden">
              <div className="aspect-[4/5] bg-cover bg-center" style={{ backgroundImage: "url(https://thetadynamics.in/assets/images/team/gcd.png)" }} />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="overline mb-4">Founder · Spotlight</div>
            <h3 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              Mr. Gajendra Deshpande
            </h3>
            <div className="mt-2 font-mono text-[11px] tracking-[0.22em] uppercase text-[#1D4ED8] font-bold">
              Founder & Managing Director
            </div>
            <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed">
              A hands-on technologist with deep experience across cyber security, AI engineering, ERP delivery and immersive technology. He leads Theta Dynamics' product and engineering teams — defining the technology roadmap, leading high-stakes security engagements, and shipping platforms used by enterprises, governments and institutions across India and the Middle East.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Under his leadership, Theta Dynamics has become an Odoo Partner, a NASSCOM member and a TGS100 startup — combining strong engineering culture with a sharp security and AI focus.
            </p>
          </div>
        </div>
      </section>

      {/* Awards & Accreditations */}
      <section className="py-20 md:py-32 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Awards & Accreditations"
            title="Awarded, accredited, audited."
            description="Theta Dynamics is a recognised Odoo Partner, NASSCOM member, MSME, AICTE / VTU / SIDM partner — registered under Start-up India and Start-up Karnataka."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {CERTIFICATIONS.map((c) => (
              <div
                key={c}
                className="card-flat p-5 flex items-start gap-3"
              >
                <CheckCircle size={18} weight="bold" className="text-[#1D4ED8] mt-0.5 shrink-0" />
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-800 leading-snug font-medium">
                  {c}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-black text-slate-900 tracking-tighter leading-[1.05]">
              Want to work with us, or partner on a programme?
            </h2>
            <p className="mt-5 text-slate-600 max-w-2xl">{COMPANY.pitch}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
              Talk to us <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
